import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import TagBadge, { parseTags } from '../components/TagBadge';
import '../components/TagBadge.css';
import UsageBar from '../components/UsageBar';
import NovaVMModal from './vmlist/NovaVMModal';
import './VMListPage.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  const left  = Math.max(1, page - delta);
  const right = Math.min(totalPages, page + delta);

  if (left > 1)           { pages.push(1); if (left > 2) pages.push('…'); }
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages) { if (right < totalPages - 1) pages.push('…'); pages.push(totalPages); }

  return (
    <div className="pagination">
      <button className="pg-btn pg-arrow" onClick={() => onPage(page - 1)} disabled={page === 1} title="Página anterior">‹</button>
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`e${i}`} className="pg-ellipsis">…</span>
          : <button key={p} className={`pg-btn${p === page ? ' pg-active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      )}
      <button className="pg-btn pg-arrow" onClick={() => onPage(page + 1)} disabled={page === totalPages} title="Próxima página">›</button>
    </div>
  );
}

function VMListPage({ isAdmin }) {
  const [vms, setVMs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [openLogVmid, setOpenLogVmid] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [haMap, setHaMap] = useState({});
  const [novaVmOpen, setNovaVmOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Lê ?status=online|offline da URL
  const statusFilter = new URLSearchParams(location.search).get('status'); // 'online'|'offline'|null
  const [vmLogs, setVmLogs] = useState({});
  const [vmCfMap, setVmCfMap] = useState({});

  useEffect(() => {
    fetchVMs();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchHA();
      fetchVmCfBilling();
    }
  }, [isAdmin]);

  // Reset de página ao mudar filtros
  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  const fetchHA = async () => {
    try {
      const res = await axios.get('/api/cluster/ha/resources');
      const resources = res.data?.data || res.data || [];
      const map = {};
      resources.forEach(r => {
        // sid format: "vm:VMID" or "ct:VMID"
        const match = r.sid?.match(/^(?:vm|ct):(\d+)$/);
        if (match) map[match[1]] = { state: r.state, group: r.group, status: r.status, maxRelocate: r.max_relocate, maxRestart: r.max_restart };
      });
      setHaMap(map);
    } catch { /* admin sem permissão ou HA não configurado */ }
  };

  const fetchVmCfBilling = async () => {
    try {
      const res = await axios.get('/api/admin/vm-cf-billing');
      setVmCfMap(res.data?.data || {});
    } catch { /* admin sem permissão */ }
  };

  const fetchVMs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/vms');
      setVMs(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Erro ao carregar VMs. Verifique a conexão com o Proxmox.');
      if (process.env.NODE_ENV !== 'production') console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVMs();
    setRefreshing(false);
  };

  const toggleLog = async (vmid) => {
    if (openLogVmid === vmid) { setOpenLogVmid(null); return; }
    setOpenLogVmid(vmid);
    if (vmLogs[vmid]) return; // já carregado
    setVmLogs(prev => ({ ...prev, [vmid]: { loading: true, entries: [] } }));
    try {
      const res = await axios.get(`/api/vms/${vmid}/logs`);
      const entries = Array.isArray(res.data) ? res.data : (res.data.logs || []);
      setVmLogs(prev => ({ ...prev, [vmid]: { loading: false, entries } }));
    } catch {
      setVmLogs(prev => ({ ...prev, [vmid]: { loading: false, entries: [] } }));
    }
  };

  const getOsIcon = (ostype) => {
    if (!ostype) return null;
    if (ostype === 'l26' || ostype === 'l24') return '🐧';
    if (ostype.startsWith('win') || ostype === 'wxp' || ostype === 'w2k') return (
      <svg width="1em" height="1em" viewBox="0 0 10 10" fill="currentColor" style={{ verticalAlign: '-0.1em' }}>
        <rect x="0" y="0" width="4.5" height="4.5"/><rect x="5.5" y="0" width="4.5" height="4.5"/>
        <rect x="0" y="5.5" width="4.5" height="4.5"/><rect x="5.5" y="5.5" width="4.5" height="4.5"/>
      </svg>
    );
    if (ostype === 'solaris') return '☀️';
    return null;
  };

  const getOsLabel = (ostype) => {
    if (!ostype) return '';
    if (ostype === 'l26' || ostype === 'l24') return 'Linux';
    if (ostype.startsWith('win') || ostype === 'wxp' || ostype === 'w2k') return 'Windows';
    if (ostype === 'solaris') return 'Solaris';
    return ostype;
  };

  const getStatusColor = (vm) => {
    if (vm.status === 'running') return 'status-running';
    if (vm.status === 'stopped') return 'status-stopped';
    return 'status-unknown';
  };

  const getStatusText = (status) => {
    return status === 'running' ? 'ON' : 'OFF';
  };

  const filteredCount = useMemo(() => vms.filter(vm => {
    const q = search.toLowerCase();
    const matchSearch = !q || vm.name?.toLowerCase().includes(q) || String(vm.vmid).includes(q);
    const matchStatus = !statusFilter
      || (statusFilter === 'online'  && vm.status === 'running')
      || (statusFilter === 'offline' && vm.status !== 'running');
    return matchSearch && matchStatus;
  }).length, [vms, search, statusFilter]);

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h1>
            {!loading
              ? `${filteredCount}${filteredCount !== vms.length ? ` de ${vms.length}` : ''} Máquinas Virtuais`
              : 'Máquinas Virtuais'
            }
          </h1>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '⟳ Atualizando...' : '⟳ Atualizar'}
          </button>
          <button className="refresh-btn" onClick={() => setNovaVmOpen(true)} title="Solicitar criação de uma nova VM">
            🖥 Solicitar nova VM
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="search-filter-row">
          <div className="search-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Buscar VM por nome ou ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')} title="Limpar busca">✕</button>
            )}
          </div>
          <div className="status-filters">
            <button
              className={`status-filter-btn${!statusFilter ? ' active' : ''}`}
              onClick={() => navigate('/')}
            >
              Todas
            </button>
            <button
              className={`status-filter-btn online${statusFilter === 'online' ? ' active' : ''}`}
              onClick={() => navigate('/?status=online')}
            >
              <span className="sf-dot" /> Online
            </button>
            <button
              className={`status-filter-btn offline${statusFilter === 'offline' ? ' active' : ''}`}
              onClick={() => navigate('/?status=offline')}
            >
              <span className="sf-dot" /> Offline
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">Carregando VMs...</div>
        ) : (() => {
          const filtered = vms
            .filter(vm => {
              const q = search.toLowerCase();
              const matchSearch = !q || vm.name?.toLowerCase().includes(q) || String(vm.vmid).includes(q);
              const matchStatus = !statusFilter
                || (statusFilter === 'online'  && vm.status === 'running')
                || (statusFilter === 'offline' && vm.status !== 'running');
              return matchSearch && matchStatus;
            })
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' }));

          if (filtered.length === 0) return (
            <div className="empty-state">
              <p>{search ? `Nenhuma VM encontrada para "${search}".` : 'Nenhuma VM encontrada.'}</p>
            </div>
          );

          const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
          const safePage   = Math.min(page, totalPages);
          const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

          return (
          <>
          <div className="vms-grid">
            {paginated.map((vm) => {
              const logState = vmLogs[vm.vmid];
              const isLogOpen = openLogVmid === vm.vmid;
              return (
                <div key={vm.vmid} className="vm-item">
                  <div
                    className={`vm-card ${getStatusColor(vm)}`}
                    onClick={() => navigate(`/vm/${vm.vmid}`)}
                  >
                    <div className="vm-header">
                      <span className={`ha-badge ${vm.status === 'running' ? 'ha-started' : 'ha-error'}`}>
                        {vm.status !== 'running' && <span className="vm-status-dot-pulse" />}
                        {getStatusText(vm.status)}
                      </span>
                      <h3>
                        {getOsIcon(vm.ostype) && (
                          <span className="vm-os-icon" title={getOsLabel(vm.ostype)}>{getOsIcon(vm.ostype)}</span>
                        )}
                        {vm.name}
                      </h3>
                    </div>

                    <div className="vm-info">
                      <span className="info-chip">ID: <strong>{vm.vmid}</strong></span>
                      <span className="info-sep">·</span>
                      <span className="info-chip">vCPU: <strong>{vm.cpus || vm.cores || '—'}</strong></span>
                      <span className="info-sep">·</span>
                      <span className="info-chip">RAM: <strong>{vm.maxmem ? Math.round(vm.maxmem / 1024 / 1024 / 1024) + ' GB' : '—'}</strong></span>
                      <span className="info-sep">·</span>
                      <span className="info-chip">Uptime: <strong>{vm.uptime ? formatUptime(vm.uptime) : '—'}</strong></span>
                      {vm.status === 'running' && (
                        <>
                          <span className="info-sep">·</span>
                          <UsageBar label="CPU" pct={vm.cpu != null ? vm.cpu * 100 : null} />
                          <span className="info-sep">·</span>
                          <UsageBar label="MEM" pct={vm.mem && vm.maxmem ? vm.mem / vm.maxmem * 100 : null} />
                        </>
                      )}
                      {vm.tags && parseTags(vm.tags).length > 0 && (
                        <>
                          <span className="info-sep">·</span>
                          {parseTags(vm.tags).map(tag => (
                            <TagBadge key={tag} tag={tag} small />
                          ))}
                        </>
                      )}
                      {isAdmin && haMap[vm.vmid] && (
                        <>
                          <span className="info-sep">·</span>
                          <span className={`ha-badge ha-${haMap[vm.vmid].state}`} title={`HA: ${haMap[vm.vmid].state}${haMap[vm.vmid].group ? ' (grupo: ' + haMap[vm.vmid].group + ')' : ''}`}>
                            HA
                          </span>
                        </>
                      )}
                      {isAdmin && vmCfMap[vm.vmid] && (
                        <>
                          <span className="info-sep">·</span>
                          <span className="vm-cf-badge" title={`Cliente Final: ${vmCfMap[vm.vmid].cf_nome}`}>
                            {vmCfMap[vm.vmid].cf_nome}
                          </span>
                          {vmCfMap[vm.vmid].billing ? (
                            <>
                              <span className="info-sep">·</span>
                              <span className={`vm-billing-badge billing-${vmCfMap[vm.vmid].billing.status?.toLowerCase()}`} title={`Cobrança: R$ ${vmCfMap[vm.vmid].billing.value?.toFixed(2)} — Venc: ${vmCfMap[vm.vmid].billing.dueDate}`}>
                                R$ {vmCfMap[vm.vmid].billing.value?.toFixed(2)} — {
                                  { RECEIVED: 'Pago', CONFIRMED: 'Pago', PENDING: 'Pendente', OVERDUE: 'Vencida' }[vmCfMap[vm.vmid].billing.status] || vmCfMap[vm.vmid].billing.status
                                }
                              </span>
                            </>
                          ) : vmCfMap[vm.vmid].asaas_id ? (
                            <>
                              <span className="info-sep">·</span>
                              <span className="vm-billing-badge billing-none" title="Sem cobrança encontrada">Sem cobrança</span>
                            </>
                          ) : null}
                        </>
                      )}
                    </div>

                    <div className="vm-actions">
                      <button
                        className={`hist-btn${isLogOpen ? ' active' : ''}`}
                        onClick={e => { e.stopPropagation(); toggleLog(vm.vmid); }}
                        title="Histórico de ações"
                      >
                        📋
                      </button>
                      <Link
                        to={`/vm/${vm.vmid}`}
                        className="action-link"
                        onClick={e => e.stopPropagation()}
                      >
                        Detalhes →
                      </Link>
                    </div>
                  </div>

                  {isLogOpen && (
                    <div className="vm-log-panel">
                      {logState?.loading ? (
                        <div className="vm-log-empty">Carregando...</div>
                      ) : !logState?.entries?.length ? (
                        <div className="vm-log-empty">Nenhuma ação registrada.</div>
                      ) : (
                        logState.entries.map((e, i) => (
                          <div key={i} className={`vm-log-row log-result-${e.result}`}>
                            <span className="vm-log-time">{new Date(e.timestamp).toLocaleString('pt-BR')}</span>
                            <span className={`vm-log-action log-action-${e.action}`}>{e.action}</span>
                            <span className="vm-log-user">{e.displayUser || e.user || '—'}</span>
                            <span className="vm-log-msg">{e.message}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="vms-pagination-row">
            <span className="vms-pagination-info">
              Página {safePage} de {totalPages} · {filtered.length} VM{filtered.length !== 1 ? 's' : ''}
            </span>
            <Pagination page={safePage} totalPages={totalPages} onPage={setPage} />
            <select
              className="vms-pagesize-select"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              title="VMs por página"
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n} / pág.</option>
              ))}
            </select>
          </div>
          </>
          );
        })()}
        {novaVmOpen && <NovaVMModal onClose={() => setNovaVmOpen(false)} />}
      </div>
    </div>
  );
}

function formatUptime(seconds) {
  if (!seconds) return '0s';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export default VMListPage;
